// PDP-only enhancement for the Rebuy recommendation carousel.
//
// The Rebuy widget renders a horizontally-scrolling product grid
// (.rebuy-product-grid) as a native overflow-x scroller. On a tall PDP that can
// swallow vertical page scrolling. Rather than patch overscroll behaviour, we
// convert the grid into an Embla carousel: Embla drives horizontal movement via
// pointer dragging, which only captures horizontal intent, so vertical page
// scrolling is never blocked (the same reason the theme uses Embla elsewhere).
//
// The widget is injected by an app block and can re-render (e.g. on variant
// change), so we watch the DOM and (re)initialise as grids appear/disappear
// rather than relying on the element existing at load.

const GRID_SELECTOR = ".rebuy-product-grid";
const VIEWPORT_CLASS = "rebuy-embla-viewport";
const PROCESSED_ATTR = "data-rebuy-embla";

// viewport element -> Embla instance, so we can tear down on re-render.
const instances = new Map();

async function mountGrid(grid) {
  if (!(grid instanceof Element) || grid.hasAttribute(PROCESSED_ATTR)) return;
  // Claim it synchronously so a concurrent DOM mutation can't double-mount.
  grid.setAttribute(PROCESSED_ATTR, "");

  let EmblaCarousel;
  try {
    ({ default: EmblaCarousel } = await import("embla-carousel"));
  } catch (err) {
    grid.removeAttribute(PROCESSED_ATTR);
    console.warn("Rebuy Embla: failed to load embla-carousel.", err);
    return;
  }

  // Rebuy may have re-rendered while the chunk loaded.
  if (!grid.isConnected) {
    grid.removeAttribute(PROCESSED_ATTR);
    return;
  }

  // Embla's contract: an overflow-hidden viewport whose first child is the
  // track it transforms. Wrap the grid so the grid becomes that track and the
  // .rebuy-product-block children become the slides.
  const viewport = document.createElement("div");
  viewport.className = VIEWPORT_CLASS;
  grid.parentNode.insertBefore(viewport, grid);
  viewport.appendChild(grid);

  const embla = EmblaCarousel(viewport, {
    align: "start",
    containScroll: "trimSnaps",
    dragFree: true, // free momentum scroll, matching the native feel it replaces
    watchResize: true,
    watchSlides: true
  });

  instances.set(viewport, embla);
}

function unmountViewport(viewport) {
  const embla = instances.get(viewport);
  if (!embla) return;
  embla.destroy();
  instances.delete(viewport);
}

function initRebuyEmbla() {
  // PDP only — body carries `template-product`, `template-product.<suffix>`, etc.
  if (!document.body || !document.body.className.includes("template-product")) return;

  // Handle any grids already present, then watch for async (re)renders.
  document.querySelectorAll(GRID_SELECTOR).forEach(mountGrid);

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches(GRID_SELECTOR)) mountGrid(node);
        node.querySelectorAll?.(GRID_SELECTOR).forEach(mountGrid);
      });

      // Tear down instances whose viewport (ours) left the DOM.
      mutation.removedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.classList.contains(VIEWPORT_CLASS)) unmountViewport(node);
        node.querySelectorAll?.(`.${VIEWPORT_CLASS}`).forEach(unmountViewport);
      });
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initRebuyEmbla, { once: true });
} else {
  initRebuyEmbla();
}

export default initRebuyEmbla;
